import { useEffect, useContext, useState, useRef } from "react";
import {
  Skeleton,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";

import MUICard from "../../../Component/UI/MUICard/MUICard";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import { DiscoveryContext } from "../discovery-context";
import { extractDomain } from "../../../utils/helpers";
import classes from "./CategoryDimensions.module.css";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import api from "../../../BrandOnBoarding/api";
import useGeneratePreCategoryDimensions from "../useGeneratePreCategoryDimensions";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { metricExplainations } from "../../../utils/constant";
import DetailedTableModal from "../../BrandAudit/BrandAuditMetricDetails";
import { formatName } from "../../Dashboard/Component/CoreAttributes/CoreAttributes";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { formatDomainData } from "../../BrandAudit/Sources/sourcesOverview";
import { useDispatch } from "react-redux";
import { formatSourceData,formatOldSourceData } from "../../../utils/metric-sources"
import { llmWeb } from "../../../services/apiService";
import BrandAvatar from "../../../Component/Avatar/Avatar"

const TABLE_HEADERS = [
  {
    id: "criteria",
    name: "Applicant Criteria",
  },
];

export default function CategoryDimensions({ entity }) {
  const [showModal, setShowModal] = useState(false);
  const discoveryCtx = useContext(DiscoveryContext);
  const { preDimensions } = useGeneratePreCategoryDimensions();
  const abortControllerRef = useRef(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const customerId = user?.customer_id;
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const type = discoveryCtx.type;
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  useEffect(() => {
    if (discoveryCtx.state.id) return;
    if (
      discoveryCtx?.category_dimensions &&
      Object.keys(discoveryCtx?.category_dimensions).length > 0 &&
      preDimensions?.length === 0
    ) {
      return;
    }

    // getCriterias();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [discoveryCtx.state.id]);

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: market == "General Market" ? "General Location" : market,
      market: "General Market",
      category,
      llm,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "70%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }

  function renderHeaders(criterias) {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors.map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              ctxCompetitor = discoveryCtx.competitors[index - 1];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <Tooltip title={header.name} placement="top">
                  <div className={classes.table_head_content}>
                    {index != 0 && (
                      // <img
                      //   data-html2canvas-ignore
                      //   className={classes.table_head_logo}
                      //   src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                      // />
                      <BrandAvatar
                        domainName={domain}
                        brandName={header.name}
                      />
                    )}
                    <div
                      className={
                        index === 0
                          ? classes.table_head_text_comp
                          : classes.table_head_text
                      }
                    >
                      {/* {header.name} */}
                      <div className={classes.table_head_title}>
                        <span>
                          {header.name}
                        </span>
                        {index === 0 && !showModal && (
                          <MUIIconButton onClick={() => setShowModal(true)} id="purches_criteria" data-testid="purchase-criteria-info-btn">
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function renderRows(rowItem, tableIndex) {
    return (
      <StyledTableRow key={tableIndex}>
        <StyledTableCell className={classes.attribute_headings}>
          {rowItem.name}
        </StyledTableCell>
        {discoveryCtx.competitors.forEach((comp, index) => {
          const exists = rowItem.brands.some(item =>
            item.name.trim().toLowerCase() === comp.name.trim().toLowerCase()
          );
          if (!exists) {
            rowItem.brands.splice(index, 0, {
              name: comp.name,
              rating: 0,
              sources: [],
              explanation: ""
            });
          }
        })}
        {rowItem.brands.map((item, index) => {
          let color = "#dc3545";
          let rating = "low";
          if (!item.rating) {
            color = "#000";
          }
          if (item.rating >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          if (item.rating >= 9) {
            color = "#198754";
            rating = "high";
          }
          const attributeSources = Array.isArray(item?.sources) && item?.sources?.length > 0 ? item?.sources : sourcesCategoryDim;
          const isCustomTooltip =
            item?.name?.toLowerCase() === discoveryCtx.entity?.toLowerCase() &&
            llm?.toLowerCase()?.includes("search") &&
            (showOldSourcesRecord || isValidated);

          const sources = isCustomTooltip
            ? (showOldSourcesRecord
              ? formatOldSourceData(attributeSources, llm)
              : formatSourceData(attributeSources, llm))
            : {};

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              metric: "Applicant Criteria",
              attribute: rowItem.name,
              metricScore: Number(item.rating).toFixed(0),
              selectedInput: {
                llm,
                market,
                category,
                brand: item.name,
                product: item.name,
              },
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              isActionLoading: isActionLoading,
              title: item.explanation,
              arrow: true,
              sources,
              isBrandOverview: true,
              isDiscovery: true,
              isBrandAudit: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }
            }
            : {
              title: item.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return (
            item.rating ? (
              <TooltipComponent {...tooltipProps}>
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  {Number(item.rating).toFixed(0)}
                </StyledTableCell>
              </TooltipComponent>)
              : (
                <StyledTableCell
                  style={{ color }}
                  className={classes.attribute_score}
                >
                  -
                </StyledTableCell>)

          );
        })}
      </StyledTableRow>
    );
  }
  let loading = true;
  let criterias = {};
  let sourcesCategoryDim = [];
  let isValidated = false;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus

  if (
    discoveryCtx?.category_dimensions &&
    Object.keys(discoveryCtx?.category_dimensions).length > 0) {
    criterias = discoveryCtx?.category_dimensions;
    sourcesCategoryDim = criterias?.sources;
    isValidated = criterias?.isValidated;
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }
  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {!loading && !hasError && criterias?.data?.length > 0 && (
        <>
          <Table
            elevation={0}
            data={criterias?.data}
            renderHeaders={() => renderHeaders(criterias?.data)}
            render={renderRows}
          />
          <DetailedTableModal
            show={showModal}
            onClose={() => setShowModal(false)}
            headers={discoveryCtx.competitors.map((item) => item.name)}
            data={criterias?.data}
            renderHeaders={renderHeaders}
            formatName={formatName}
            classes={classes}
            pdfHeight={300}
            pdfTitle={`${entity}-Applicant Criteria`}
          />
        </>
      )}
      {hasError && (
        <div className={classes.error}>
          <ErrorView
            title={"Error Fetching Applicant Criteria"}
            message={"Request Failed"}
          />
        </div>
      )}
      {loading && !hasError && <LoadingSkeletion />}
    </MUICard>
  );
}
